import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../redux/helpers/axios";
import { CDN } from "../redux/helpers/urlConfig";

/**
 * @author
 * @function Payment
 **/

const STATUS_STYLES = {
  PENDING: "bg-yellow-100 text-yellow-800",
  PENDING_APPROVAL: "bg-orange-100 text-orange-800",
  SUCCESS: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
};

export const Payment = (props) => {
  const [payments, setPayments] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    per_page: 10,
    total_pages: 1,
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [status, setStatus] = useState("");
  const [gateway, setGateway] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedPayment, setSelectedPayment] = useState(null); // for modal
  const [approvingPayment, setApprovingPayment] = useState(null); // for bank deposit approve modal

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("/admin/payment/services/get-all", {
        params: {
          page,
          per_page: perPage,
          ...(status ? { status } : {}),
          ...(gateway ? { gateway } : {}),
        },
      });

      setPayments(res.data.payments);
      setMeta(res.data.meta);
    } catch (err) {
      setError(
        err.response?.data?.msg || err.message || "Failed to fetch payments",
      );
    } finally {
      setLoading(false);
    }
  }, [page, perPage, status, gateway]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleFilterChange = (setter) => (e) => {
    setPage(1); // reset to page 1 whenever a filter changes
    setter(e.target.value);
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Payments</h2>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={status}
          onChange={handleFilterChange(setStatus)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2F5651]/40 focus:border-[#2F5651]"
        >
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="PENDING_APPROVAL">Pending Approval</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILED">Failed</option>
        </select>

        <select
          value={gateway}
          onChange={handleFilterChange(setGateway)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2F5651]/40 focus:border-[#2F5651]"
        >
          <option value="">All Gateways</option>
          <option value="BKASH">bKash</option>
          <option value="CARD">Card</option>
          <option value="BANK_TRANSFER">Bank Transfer</option>
          <option value="OTHER">Other</option>
        </select>

        <select
          value={perPage}
          onChange={(e) => {
            setPage(1);
            setPerPage(Number(e.target.value));
          }}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2F5651]/40 focus:border-[#2F5651]"
        >
          <option value={10}>10 / page</option>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
        </select>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {loading && (
          <div className="p-6 text-center text-gray-500 text-sm">
            Loading...
          </div>
        )}
        {error && (
          <div className="p-4 text-center text-red-600 text-sm bg-red-50">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-[#2F5651] text-white text-left">
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Payment Key</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Currency</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Gateway</th>
                  <th className="px-4 py-3 font-medium">Created At</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-gray-400"
                    >
                      No payments found.
                    </td>
                  </tr>
                ) : (
                  payments.map((p) => (
                    <tr
                      key={p.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-700">{p.id}</td>
                      <td className="px-4 py-3 text-gray-700 font-mono text-xs">
                        {p.paymentKey}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {p.paidAmount}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{p.currency}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            STATUS_STYLES[p.status] ||
                            "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {p.gateway || "-"}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {new Date(p.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right space-x-3">
                        <button
                          onClick={() => setSelectedPayment(p)}
                          className="text-[#2F5651] hover:underline font-medium"
                        >
                          View
                        </button>
                        {p.gateway === "BANK_TRANSFER" &&
                          p.status === "PENDING_APPROVAL" && (
                            <button
                              onClick={() => setApprovingPayment(p)}
                              className="text-green-700 hover:underline font-medium"
                            >
                              Approve
                            </button>
                          )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {meta.page} of {meta.total_pages || 1} ({meta.total} total)
          </span>
          <button
            disabled={page >= meta.total_pages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Next
          </button>
        </div>
      </div>

      {selectedPayment && (
        <PaymentDetailsModal
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}

      {approvingPayment && (
        <ApproveBankDepositModal
          payment={approvingPayment}
          onClose={() => setApprovingPayment(null)}
          onApproved={() => {
            setApprovingPayment(null);
            fetchPayments();
          }}
        />
      )}
    </div>
  );
};

const PaymentDetailsModal = ({ payment, onClose }) => {
  const attachment = payment.attachment; // Json field — { key, path }

  const renderAttachment = () => {
    if (!attachment?.path)
      return <p className="text-sm text-gray-400">No attachment.</p>;

    return (
      <div className="flex flex-wrap gap-3">
        <a
          href={`${CDN}${attachment.path}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={`${CDN}${attachment.path}`}
            alt={`Attachment for payment ${payment.id}`}
            className="w-32 h-32 object-cover rounded-md border border-gray-200 hover:opacity-90 transition-opacity"
          />
        </a>
      </div>
    );
  };

  const Row = ({ label, value }) => (
    <tr className="border-b border-gray-100 last:border-0">
      <td className="py-2 pr-4 text-gray-500 font-medium w-40">{label}</td>
      <td className="py-2 text-gray-800">{value}</td>
    </tr>
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="text-base font-semibold text-gray-800">
            Payment Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4">
          <table className="w-full text-sm">
            <tbody>
              <Row label="ID" value={payment.id} />
              <Row label="Payment Key" value={payment.paymentKey} />
              <Row label="Payment ID" value={payment.paymentId || "-"} />
              <Row label="Requested Amount" value={payment.requestedAmount} />
              <Row label="Paid Amount" value={payment.paidAmount} />
              <Row label="Currency" value={payment.currency} />
              <Row
                label="Status"
                value={
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      STATUS_STYLES[payment.status] ||
                      "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {payment.status}
                  </span>
                }
              />
              <Row label="Gateway" value={payment.gateway || "-"} />
              <Row label="Gateway Trx ID" value={payment.gatewayTrxId || "-"} />
              <Row label="Ref ID" value={payment.refId || "-"} />
              <Row label="Remarks" value={payment.remarks || "-"} />
              <Row
                label="Created At"
                value={new Date(payment.createdAt).toLocaleString()}
              />
              <Row
                label="Updated At"
                value={new Date(payment.updatedAt).toLocaleString()}
              />
            </tbody>
          </table>

          <div className="mt-4">
            <div className="text-sm font-medium text-gray-500 mb-2">
              Attachment
            </div>
            {renderAttachment()}
          </div>
        </div>
      </div>
    </div>
  );
};

const ApproveBankDepositModal = ({ payment, onClose, onApproved }) => {
  // Auto-populated from payment info; only confirmedAmount is admin-editable
  const [confirmedAmount, setConfirmedAmount] = useState(
    payment.requestedAmount ?? "",
  );
  const gatewayTrxId = payment.gatewayTrxId || payment.refId || "";
  const refId = payment.refId || "";

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    if (!confirmedAmount || !gatewayTrxId || !refId) {
      setError("Confirmed amount, gateway trx ID, and ref ID are required.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await axiosInstance.post(
        `/payment/services/bank/approve-payment/${payment.paymentKey}`,
        {
          confirmedAmount: Number(confirmedAmount),
          gatewayTrxId,
          refId,
        },
      );
      onApproved();
    } catch (err) {
      setError(
        err.response?.data?.msg ||
          err.message ||
          "Failed to approve bank deposit",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">
            Approve Bank Deposit
          </h3>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-700 text-lg leading-none disabled:opacity-40"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Payment Key
            </label>
            <input
              type="text"
              value={payment.paymentKey || ""}
              disabled
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Gateway Trx ID
            </label>
            <input
              type="text"
              value={gatewayTrxId}
              disabled
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Ref ID
            </label>
            <input
              type="text"
              value={refId}
              disabled
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Confirmed Amount
            </label>
            <input
              type="number"
              value={confirmedAmount}
              onChange={(e) => setConfirmedAmount(e.target.value)}
              disabled={submitting}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2F5651]/40 focus:border-[#2F5651]"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-2 rounded-md bg-[#2F5651] text-white text-sm font-medium hover:bg-[#264541] disabled:opacity-50"
          >
            {submitting ? "Confirming..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};
